import { CheckOutlined, CloseSquareOutlined, DeleteOutlined, EditOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, InputNumber, notification, Row, Select, Upload } from "antd";
import React, { memo, useState } from "react";
import { IConsumableEditFormValues, IConsumableEditItemProps } from "@Components/consumables/types";
import { useDeleteConsumable, useUpdateConsumable } from "./mutations";
import IconButton from "@Components/shared/IconButton";

const ConsumableEditItem: React.FC<IConsumableEditItemProps> = (props: IConsumableEditItemProps) => {
    const [isEdit, setIsEdit] = useState<boolean>(false);

    const { mutateAsync: deleteConsumable, isLoading: isDeleting } = useDeleteConsumable();
    const { mutateAsync: updateConsumables, isLoading: isUpdating } = useUpdateConsumable(props.consumableId);

    const handleEditFormSubmit = async (values: IConsumableEditFormValues) => {
        await updateConsumables(values).then((res) => {
            if (!res.hasErrors) {
                notification.success({ message: "Item updated." });
            } else {
                notification.error({ message: "Error updating item." });
            }
            props.getAllConsumables();
            setIsEdit(false);
        });
    };

    const handleConsumableDelete = (id: string) => {
        deleteConsumable(id, {
            onSuccess: (res) => {
                if (res.value) {
                    notification.success({ message: "Item deleted." });
                } else {
                    notification.error({ message: "Error deleting item." });
                }
                props.getAllConsumables();
            },
        });
    };

    const isLoading = (isDeleting || isUpdating) ?? false;

    const handleEdit = () => {
        setIsEdit((prevState) => !prevState);
    };

    const onBeforeUpload = (file) => {
        const types = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
        const valid = types.includes(file.type);
        if (!valid) {
            notification.error({ message: "Image file type is invalid." });
        }
        return valid || Upload.LIST_IGNORE;
    };

    // "normFile" fixing antd warning: [antd: Upload] `value` is not a valid prop, do you mean `fileList`?
    const normFile = (e: any) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e && e.fileList;
    };

    if (isEdit) {
        let selectedProducts = [];
        let defaultSelectOptions = [];
        if (props.productIds.length) {
            selectedProducts = props.products.filter((item) => {
                const prod: { id: string } = props.productIds.find((product) => product.id === item.productId);
                return item.productId === prod?.id;
            });
            defaultSelectOptions = selectedProducts.map((item) => ({ value: item.productId, label: item.label }));
        }
        const selectOptions = props.products.map((item) => ({ value: item.productId, label: item.label }));

        return (
            <Form
                name="consumable-edit"
                className="consumable-edit-form newUI-glass"
                layout="vertical"
                onFinish={handleEditFormSubmit}
                initialValues={{
                    name: props.name,
                    productCode: props.productCode,
                    creditPrice: props.creditPrice,
                    productIds: [...defaultSelectOptions.map((item) => item.value)],
                    description: props.description,
                }}
            >
                <Row gutter={[8, 16]}>
                    <Col md={12} xs={24}>
                        <Form.Item
                            name="name"
                            label="Product Name"
                            rules={[{ required: true, message: "Please give the product a name" }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col md={12} xs={24}>
                        <Form.Item
                            name="productCode"
                            label="Product Code"
                            rules={[
                                { whitespace: true, message: "Please do not use spaces in the product code" },
                                { required: true, message: "Please enter a product code" },
                            ]}
                            normalize={(value) => value.replace(/\s/g, "")}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[8, 16]}>
                    <Col md={12} xs={24}>
                        <Form.Item
                            name="creditPrice"
                            label="Credit Value"
                            rules={[{ required: true, message: "Please input a price" }]}
                        >
                            <InputNumber min={0} />
                        </Form.Item>
                    </Col>
                    <Col md={12} xs={24}>
                        <Form.Item
                            name="productIds"
                            label="Products"
                            rules={[{ required: true, message: "Please select a product" }]}
                        >
                            <Select mode="multiple" allowClear options={selectOptions} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[8, 16]}>
                    <Col span={24}>
                        <Form.Item name="description" label="Description">
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="imageUrl" valuePropName="fileList" getValueFromEvent={normFile} label="Image">
                    <Upload
                        maxCount={1}
                        className="upload-file"
                        accept={"image/png, image/jpeg, image/jpg, image/webp"}
                        beforeUpload={(file) => onBeforeUpload(file)}
                        multiple={false}
                    >
                        <IconButton className="secondary" icon={<UploadOutlined />}>
                            Image
                        </IconButton>
                    </Upload>
                </Form.Item>
                <div className="actions">
                    <Form.Item>
                        <IconButton
                            className="secondary"
                            disabled={isLoading}
                            loading={isUpdating}
                            htmlType="submit"
                            icon={<CheckOutlined />}
                        >
                            Update
                        </IconButton>
                    </Form.Item>
                    <Form.Item>
                        <IconButton
                            className="secondary danger"
                            disabled={isLoading}
                            icon={<CloseSquareOutlined />}
                            onClick={handleEdit}
                        >
                            Close
                        </IconButton>
                    </Form.Item>
                </div>
            </Form>
        );
    }
    return (
        <div className="consumable-edit-item newUI-glass">
            <div
                className="consumable-image"
                style={{
                    borderRadius: "10px",
                    width: "180px",
                    height: "180px",
                    backgroundImage: "url(" + props.imageUrl + ")",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            ></div>
            <div className="description">
                <table className="consumable-item-table">
                    <tbody>
                        <tr>
                            <td>
                                <b>Name</b>
                            </td>
                            <td>{props.name}</td>
                        </tr>
                        <tr>
                            <td>
                                <b>Code</b>
                            </td>
                            <td>{props.productCode}</td>
                        </tr>
                        <tr>
                            <td>
                                <b>Credits</b>
                            </td>
                            <td>{props.creditPrice}</td>
                        </tr>
                        {props.description !== null && props.description !== "" && (
                            <tr>
                                <td>
                                    <b>Description</b>
                                </td>
                                <td>{props.description}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="actions">
                <IconButton className="secondary" disabled={isLoading} icon={<EditOutlined />} onClick={handleEdit}>
                    Edit
                </IconButton>
                <IconButton
                    className="secondary danger"
                    disabled={isLoading}
                    icon={<DeleteOutlined />}
                    loading={isDeleting}
                    onClick={() => handleConsumableDelete(props.consumableId)}
                >
                    Delete
                </IconButton>
            </div>
        </div>
    );
};

export default memo(ConsumableEditItem);
